import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import adminApiService from '../../services/adminApiService';
import { toast } from 'react-toastify';

const AdminPlatformSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    platformFee: '',
    gstRate: ''
  });

  useEffect(() => {
    fetchPlatformSettings();
  }, []);

  const fetchPlatformSettings = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.getPlatformSettings();
      if (response.success) {
        setSettings({
          platformFee: response.data.platformFee || '',
          gstRate: response.data.gstRate || ''
        });
      } else {
        toast.error(response.message || 'Failed to fetch platform settings');
      }
    } catch (error) {
      console.error('Error fetching platform settings:', error);
      toast.error('Failed to fetch platform settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await adminApiService.updatePlatformSettings({
        platformFee: parseFloat(settings.platformFee),
        gstRate: parseFloat(settings.gstRate)
      });
      
      if (response.success) {
        toast.success('Platform settings updated successfully!');
      } else {
        toast.error(response.message || 'Failed to update platform settings');
      }
    } catch (error) {
      console.error('Error updating platform settings:', error);
      toast.error('Failed to update platform settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Platform Fee & GST Settings</h1>
            <p className="text-gray-600 mt-2">Configure platform fees and GST rates for bookings</p>
          </div>
          <Link 
            to="/admin/dashboard/settings" 
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            ← Back to Settings
          </Link>
        </div>
      </div>

      {/* Settings Form */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
            Platform Fee & GST Settings
          </h2>
        </div>
        
        <div className="p-6">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Platform Fee */}
              <div>
                <label htmlFor="platformFee" className="block text-sm font-semibold text-gray-700 mb-2">
                  Platform Fee (in ₹)
                </label>
                <input
                  type="number"
                  id="platformFee"
                  name="platformFee"
                  value={settings.platformFee}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Enter fee amount"
                  required
                />
                <p className="mt-1 text-sm text-gray-500">Fixed fee charged per booking.</p>
              </div>

              {/* GST Rate */}
              <div>
                <label htmlFor="gstRate" className="block text-sm font-semibold text-gray-700 mb-2">
                  GST Rate (%)
                </label>
                <input
                  type="number"
                  id="gstRate"
                  name="gstRate"
                  value={settings.gstRate}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Enter GST %"
                  required
                />
                <p className="mt-1 text-sm text-gray-500">GST applicable on platform fee (e.g., 18%).</p>
              </div>
            </div>

            {/* Preview Section */}
            {settings.platformFee && settings.gstRate && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Fee Calculation Preview</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex justify-between">
                    <span>Platform Fee:</span>
                    <span>₹{parseFloat(settings.platformFee).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>GST ({settings.gstRate}%):</span>
                    <span>₹{(parseFloat(settings.platformFee) * parseFloat(settings.gstRate) / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-semibold border-t pt-1">
                    <span>Total Fee:</span>
                    <span>₹{(parseFloat(settings.platformFee) + (parseFloat(settings.platformFee) * parseFloat(settings.gstRate) / 100)).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className={`px-6 py-2 rounded-md font-semibold text-white transition-colors duration-200 ${
                  saving 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500'
                }`}
              >
                {saving ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Save Settings
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Information Section */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="text-sm font-semibold text-blue-800 mb-1">Important Information</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Platform fee is charged per booking transaction</li>
              <li>• GST is calculated on the platform fee amount</li>
              <li>• Changes will apply to all new bookings immediately</li>
              <li>• Existing bookings will not be affected by these changes</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPlatformSettings;